import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { NameFormat } from 'src/API';
import { noSort } from 'src/app/core/api/model-utils';
import { InteractionSignoffComponent } from 'src/app/signature/interaction-signoff/interaction-signoff.component';
import { InteractionSignOffGroup } from 'src/app/signature/interaction-signoff/interaction-signoff.model';
import { SUPPORT_EMAIL_ACCESSOR } from '../../../core/domain-accessor';
import { DataSrcService } from '../../enlargeable-media/data-src.service';
import { PersonNamePipe } from '../../shared-pipes/person-name.pipe';
import { phoneNumberAndCountryCodeValidator } from '../../shared-validators/phone-number-validator';
import { DoctorDegree } from './../../../../API';
import { StaffService } from './../../../core/api/staff.service';
import { ChangePasswordModalComponent } from './change-password-modal/change-password-modal.component';
import { DoctorFormGroup } from './doctor-form.model';
import { mfaValidator } from './select-mfa-method/mfa-validator';

@Component({
  selector: 'csi-doctor-form',
  templateUrl: './doctor-form.component.html',
  styleUrls: ['./doctor-form.component.scss'],
  providers: [PersonNamePipe]
})
export class DoctorFormComponent implements OnInit {
  @Input() doctorFormGroup: DoctorFormGroup;
  @Input() showRequiredFields = false;
  @Input() hideTitle = false;
  @Input() showEmail = false;
  @Input() showInCard = true;
  @Input() showStaffType = false;

  @Input() showPasswordChange = true;

  public dummyPasswordFormControl: FormControl = new FormControl({
    value: '********',
    disabled: true
  });
  public combinedCountryCodePhoneNumberControl: FormControl = new FormControl({
    value: 'N/A',
    disabled: true
  });

  public readonly NameFormat = NameFormat;

  public readonly DoctorDegree = DoctorDegree;
  public allNameFormats = Object.keys(NameFormat);
  public noSort = noSort;
  public isDoctorOrOwner: boolean;

  constructor(
    @Inject(SUPPORT_EMAIL_ACCESSOR) public supportEmail: string,
    private dialog: MatDialog,
    public staffService: StaffService,
    public personNamePipe: PersonNamePipe,
    public dataSrcService: DataSrcService
  ) {}

  ngOnInit() {
    this.initializeNameFormat();
    this.doctorFormGroup.setValidators([
      phoneNumberAndCountryCodeValidator('phone', 'countryCode'),
      mfaValidator
    ]);
    this.doctorFormGroup.updateValueAndValidity();

    if (this.doctorFormGroup.controls.phone.value) {
      this.combinedCountryCodePhoneNumberControl.setValue(
        this.doctorFormGroup.controls.countryCode.value + this.doctorFormGroup.controls.phone.value
      );
    }

    const currentSignature = this.doctorFormGroup.controls.doctorSignature.value;
    if (currentSignature && currentSignature.drawn && typeof currentSignature.drawn === 'string') {
      try {
        this.doctorFormGroup.controls.doctorSignature.patchValue({
          drawn: JSON.parse(currentSignature.drawn)
        });
      } catch (e) {
        console.error('Error parsing stored signature');
      }
    }

    if (!this.doctorFormGroup.controls.doctorSignature.value) {
      this.doctorFormGroup.controls.doctorSignature.setValue({
        drawn: null,
        typed: null,
        uploaded: null
      });
    }

    if (this.staffService.isTech || this.staffService.isReceptionist) {
      this.doctorFormGroup.controls.displayedNameFormat.disable();
      this.doctorFormGroup.controls.doctorDegree.disable();
      this.doctorFormGroup.controls.practitionerId.disable();
      this.doctorFormGroup.controls.doctorSignature.disable();
    } else {
      this.isDoctorOrOwner = true;
    }
  }

  initializeNameFormat() {
    if (!this.doctorFormGroup.controls.displayedNameFormat.value) {
      if (
        this.staffService.isDoctor ||
        this.staffService.isClinicOwner ||
        this.staffService.isClinicAdmin
      ) {
        this.doctorFormGroup.controls.displayedNameFormat.setValue(
          NameFormat.TitleWithAbbreviatedFirstAndFullLastName
        );
      } else {
        this.doctorFormGroup.controls.displayedNameFormat.setValue(NameFormat.Fullname);
      }
    }
  }

  onChangePassword() {
    this.dialog.open(ChangePasswordModalComponent);
  }

  get hasSignature(): boolean {
    const signatureObj = this.doctorFormGroup.controls.doctorSignature.value;
    return !!(
      (signatureObj && signatureObj.drawn) ||
      (signatureObj && signatureObj.typed) ||
      (signatureObj && signatureObj.uploaded)
    );
  }

  openSignatureUpload() {
    const initialData = this.doctorFormGroup.controls.doctorSignature.value || {};

    const initialSignOff = new InteractionSignOffGroup();
    initialSignOff.patchValue({
      drawnSignature: initialData.drawn,
      typedSignature: initialData.typed,
      uploadedSignature: initialData.uploaded,
      signatureType: initialData.type,
      doctorSigning: this.staffService.staff.id,
      dateAndTime: new Date().toISOString()
    });

    const dialogRef = this.dialog.open(InteractionSignoffComponent, {
      data: {
        isProfileSignature: true,
        doctorId: this.staffService.staff.id,
        interactionSignOff: initialSignOff
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const updatedSignature = {
          drawn: null,
          typed: null,
          uploaded: null,
          type: result.signatureType
        };

        if (result.signatureType === 'upload') {
          updatedSignature.uploaded = result.uploadedSignature;
        } else {
          updatedSignature[result.signatureType] = result[`${result.signatureType}Signature`];
        }

        this.doctorFormGroup.controls.doctorSignature.patchValue(updatedSignature);
        this.doctorFormGroup.markAsDirty();
      }
    });
  }

  removeSignature() {
    const resetSignature = {
      drawn: null,
      typed: null,
      uploaded: null,
      type: null
    };
    this.doctorFormGroup.controls.doctorSignature.patchValue(resetSignature);
    this.doctorFormGroup.markAsDirty();
  }
}
